import { Injectable } from '@angular/core';
import { Action, ActionsSubject } from '@ngrx/store';
import { longMessageNotificationDuration } from 'app/common/app-constants';
import { NotificationModel } from 'app/models/dto/notification-model';
import { AberrantQuantityActions } from 'app/store/aberrant-quantity/aberrant-quantity.actions';
import { AccountActions } from 'app/store/account/account.actions';
import { ActionRequestPayload } from 'app/store/action-request-payload';
import { ActionResponsePayload } from 'app/store/action-response-payload';
import { BdcActions } from 'app/store/bdc/bdc.actions';
import { CustomerBdcActions } from 'app/store/customer-bdc/customer-bdc.actions';
import { CustomerCustomDateOfServiceActions } from 'app/store/customer-custom-date-of-service/customer-custom-date-of-service.actions';
import { CustomerActions } from 'app/store/customer/customer.actions';
import { ExcludedPharmacyActions } from 'app/store/excluded-pharmacy/excluded-pharmacy.actions';
import { FacilityActions } from 'app/store/facility/facility.actions';
import { InOutpatientDefinitionActions } from 'app/store/in-outpatient-definition/in-outpatient-definition.actions';
import { RebateGroupAggregatorNdcActions } from 'app/store/rebate-group-aggregator-ndc/rebate-group-aggregator-ndc.actions';
import { CustomerNdcDestinationActions } from 'app/store/customer-ndc-destination/customer-ndc-destination.actions';
import { LineOfBusinessSubtypeActions } from 'app/store/line-of-business-subtype/line-of-business-subtype.actions';
import { LineOfBusinessActions } from 'app/store/line-of-business/line-of-business.actions';
import { NdcReplacementMappingsActions } from 'app/store/ndc-replacement-mappings/ndc-replacement-mappings.actions';
import { PayorsActions } from 'app/store/payors/payors.actions';
import { RebateAggregatorNdcRatesActions } from 'app/store/rebate-aggregator-ndc-rates/rebate-aggregator-ndc-rates.actions';
import { RebateAggregatorFormularyActions } from 'app/store/rebate-aggregator-formulary/rebate-aggregator-formulary.actions';
import { FormularyNdcActions } from 'app/store/formulary-ndc/formulary-ndc.actions';
import { RebateGroupActions } from 'app/store/rebate-group/rebate-group.actions';
import { RebatesLoadActions } from 'app/store/rebates-load/rebates-load.actions';
import { RebateAggregatorActions } from 'app/store/rebate-aggregator/rebate-aggregator.actions';
import { RebatesReSubmissionActions } from 'app/store/rebates-re-submission/rebates-re-submission.actions';
import { RebatesUpdateActions } from 'app/store/rebates-update/rebates-update.actions';
import { ReportActions } from 'app/store/report/report.actions';
import { SubmissionPeriodActions } from 'app/store/submission-period/submission-period.actions';
import { UnbreakableQuantityActions } from 'app/store/unbreakable-quantity/unbreakable-quantity.actions';
import { NonPatientExclusionActions } from 'app/store/non-patient-exclusion/non-patient-exclusion.actions';
import { UserActions } from 'app/store/user/user.actions';
import { skip } from 'rxjs';
import { match } from 'ts-pattern';
import { ProcessingConfigActions } from 'app/store/processing-config/processing-config.actions';
import { NotificationType } from 'app/models/domain/notification/notification-type';
import { CheckResultModel } from '@twrx-api-models';
import { NdcDestinationHistoryActions } from 'app/store/ndc-destination-history/ndc-destination-history.actions';
import { NdcDestinationImportActions } from 'app/store/ndc-destination-import/ndc-destination-import.actions';
import { SubmissionOverviewActions } from 'app/store/submission-overview/submission-overview.actions';
import { SubmissionFilesActions } from 'app/store/submission-files/submission-files.actions';

import { ToastService } from './toast.service';

type ActionPayload = Action
	& (ActionRequestPayload<unknown> | ActionResponsePayload<unknown>);

@Injectable({ providedIn: 'root' })
export class EventNotifierService {
	constructor(
		private readonly actionSubject: ActionsSubject,
		private readonly toastService: ToastService
	) {}

	public startListen(): void {
		this.actionSubject
			.pipe(skip(1))
			.subscribe((actionPayload: ActionPayload) => {
				const notificationData =
					this.getNotificationData(actionPayload);

				if (notificationData) {
					this.toastService.show(notificationData);
				}
			});
	}

	private getNotificationData({
		type,
		data
	}: ActionPayload): NotificationModel {
		return match<string, NotificationModel>(type)
			.with(AccountActions.CurrentUserInfoSaved, () => ({
				translationPath: 'account.success.currentUserInfoSaved'
			}))
			.with(AccountActions.PasswordResetConfirmed, () => ({
				translationPath: 'account.success.passwordResetConfirmed'
			}))
			.with(AccountActions.ForgotPasswordRequestSent, () => ({
				translationPath: 'account.success.forgotPasswordEmailWasSend',
				duration:        longMessageNotificationDuration
			}))
			.with(AccountActions.CurrentUserPasswordReseted, () => ({
				translationPath:
					'account.success.resetCurrentUserPasswordEmailSent',
				duration: longMessageNotificationDuration
			}))
			.with(AccountActions.GeneratedFileDeleted, () => ({
				translationPath: 'generatedFiles.success.fileDeleted',
				duration:        longMessageNotificationDuration
			}))
			.with(UserActions.UserDeleted, () => ({
				translationPath: 'user.success.userDeleted'
			}))
			.with(UserActions.EditableUserCreated, () => ({
				translationPath: 'user.success.userSaved'
			}))
			.with(UserActions.EditableUserUpdated, () => ({
				translationPath: 'user.success.userSaved'
			}))
			.with(UserActions.RoleCreated, () => ({
				translationPath: 'user.success.roleSaved'
			}))
			.with(UserActions.RoleUpdated, () => ({
				translationPath: 'user.success.roleSaved'
			}))
			.with(CustomerActions.CustomerCreated, () => ({
				translationPath: 'customer.success.customerSaved'
			}))
			.with(CustomerActions.CustomerUpdated, () => ({
				translationPath: 'customer.success.customerSaved'
			}))
			.with(FacilityActions.CustomerFacilityCreated, () => ({
				translationPath: 'facility.success.facilitySaved'
			}))
			.with(FacilityActions.CustomerFacilityUpdated, () => ({
				translationPath: 'facility.success.facilitySaved'
			}))
			.with(FacilityActions.FacilityDeactivated, () => ({
				translationPath: 'facility.success.facilityDeleted'
			}))
			.with(FacilityActions.LocationEnabled, () => ({
				translationPath: 'facility.success.locationEnabled'
			}))
			.with(FacilityActions.LocationsEnabled, () => ({
				translationPath: 'facility.success.locationsEnabled'
			}))
			.with(FacilityActions.LocationsFileGenerationStarted, () => ({
				translationPath:
					'facility.success.locationsFileGenerationStarted'
			}))
			.with(FacilityActions.LocationMapped, () => ({
				translationPath: 'facility.success.locationMapped'
			}))
			.with(FacilityActions.LocationsMapped, () => ({
				translationPath: 'facility.success.locationsMapped'
			}))
			.with(FacilityActions.AllLocationsMapped, () => ({
				translationPath: 'facility.success.allLocationsMapped'
			}))
			.with(CustomerActions.RetentionRateCreated, () => ({
				translationPath: 'customer.success.retentionRateCreated'
			}))
			.with(CustomerActions.RetentionRateUpdated, () => ({
				translationPath: 'customer.success.retentionRateUpdated'
			}))
			.with(CustomerActions.RetentionRateDeleted, () => ({
				translationPath: 'customer.success.retentionRateDeleted'
			}))
			.with(CustomerActions.TechEnablementFeeCreated, () => ({
				translationPath: 'customer.success.techEnablementFeeCreated'
			}))
			.with(CustomerActions.TechEnablementFeeUpdated, () => ({
				translationPath: 'customer.success.techEnablementFeeUpdated'
			}))
			.with(CustomerActions.TechEnablementFeeDeleted, () => ({
				translationPath: 'customer.success.techEnablementFeeDeleted'
			}))
			.with(CustomerActions.NoteCreated, () => ({
				translationPath: 'customer.success.noteCreated'
			}))
			.with(CustomerActions.NoteUpdated, () => ({
				translationPath: 'customer.success.noteUpdated'
			}))
			.with(CustomerActions.RecentNoteUpdated, () => ({
				translationPath: 'customer.success.noteUpdated'
			}))
			.with(CustomerActions.NoteDeleted, () => ({
				translationPath: 'customer.success.noteDeleted'
			}))
			.with(CustomerActions.NoteRestored, () => ({
				translationPath: 'customer.success.noteRestored'
			}))
			.with(
				CustomerActions.CustomerLocationsFileGenerationStarted,
				() => ({
					translationPath:
						'customer.success.customerLocationsFileGenerationStarted'
				})
			)
			.with(CustomerBdcActions.CustomerBdcCreated, () => ({
				translationPath: 'customer.success.thirdPartyAssociationCreated'
			}))
			.with(CustomerBdcActions.CustomerBdcUpdated, () => ({
				translationPath: 'customer.success.thirdPartyAssociationUpdated'
			}))
			.with(CustomerBdcActions.CustomerBdcDeleted, () => ({
				translationPath: 'customer.success.thirdPartyAssociationDeleted'
			}))
			.with(
				CustomerCustomDateOfServiceActions.CustomDateOfServiceCreated,
				() => ({
					translationPath:
						'customer.success.customDateOfServiceCreated'
				})
			)
			.with(
				CustomerCustomDateOfServiceActions.CustomDateOfServiceUpdated,
				() => ({
					translationPath:
						'customer.success.customDateOfServiceUpdated'
				})
			)
			.with(
				CustomerCustomDateOfServiceActions.CustomDateOfServiceDeleted,
				() => ({
					translationPath:
						'customer.success.customDateOfServiceDeleted'
				})
			)
			.with(AberrantQuantityActions.CreateAberrantQuantity, () => ({
				translationPath:
					'aberrantQuantity.success.aberrantQuantityCreated'
			}))
			.with(AberrantQuantityActions.AberrantQuantityUpdated, () => ({
				translationPath:
					'aberrantQuantity.success.aberrantQuantityUpdated'
			}))
			.with(AberrantQuantityActions.AberrantQuantityDeleted, () => ({
				translationPath:
					'aberrantQuantity.success.aberrantQuantityDeleted'
			}))
			.with(BdcActions.BdcCreated, () => ({
				translationPath: 'bdc.success.thirdPartyCreated'
			}))
			.with(BdcActions.BdcUpdated, () => ({
				translationPath: 'bdc.success.thirdPartyUpdated'
			}))
			.with(BdcActions.DeleteBdc, () => ({
				translationPath: 'bdc.success.thirdPartyDeleted'
			}))
			.with(RebateAggregatorActions.RebateAggregatorCreated, () => ({
				translationPath:
					'rebateAggregator.success.rebateAggregatorCreated'
			}))
			.with(RebateAggregatorActions.RebateAggregatorUpdated, () => ({
				translationPath:
					'rebateAggregator.success.rebateAggregatorUpdated'
			}))
			.with(
				RebateAggregatorActions.RebateAggregatorMappingValidationUpdated,
				() => ({
					translationPath:
						'rebateAggregator.success.rebateAggregatorMappingConfigUpdated'
				})
			)
			.with(
				RebateAggregatorFormularyActions.RebateAggregatorFormularyCreated,
				() => ({
					translationPath:
						'rebateAggregator.success.rebateAggregatorFormularyCreated'
				})
			)
			.with(
				RebateAggregatorFormularyActions.RebateAggregatorFormularyUpdated,
				() => ({
					translationPath:
						'rebateAggregator.success.rebateAggregatorFormularyUpdated'
				})
			)
			.with(
				RebateAggregatorFormularyActions.RebateAggregatorFormularyDeleted,
				() => ({
					translationPath:
						'rebateAggregator.success.rebateAggregatorFormularyDeleted'
				})
			)
			.with(
				RebateGroupAggregatorNdcActions.RebateGroupAggregatorNdcCreated,
				() => ({
					translationPath:
						'rebateGroupAggregatorNdc.success.rebateGroupAggregatorNdcCreated'
				})
			)
			.with(
				RebateGroupAggregatorNdcActions.RebateGroupAggregatorNdcUpdated,
				() => ({
					translationPath:
						'rebateGroupAggregatorNdc.success.rebateGroupAggregatorNdcUpdated'
				})
			)
			.with(
				RebateGroupAggregatorNdcActions.RebateGroupAggregatorNdcDeleted,
				() => ({
					translationPath:
						'rebateGroupAggregatorNdc.success.rebateGroupAggregatorNdcDeleted'
				})
			)
			.with(
				CustomerNdcDestinationActions.CustomerNdcDestinationCreated,
				() => ({
					translationPath:
						'rebateGroupAggregatorNdc.success.customerNdcDestinationCreated'
				})
			)
			.with(
				CustomerNdcDestinationActions.CustomerNdcDestinationUpdated,
				() => ({
					translationPath:
						'rebateGroupAggregatorNdc.success.customerNdcDestinationUpdated'
				})
			)
			.with(
				CustomerNdcDestinationActions.CustomerNdcDestinationDeleted,
				() => ({
					translationPath:
						'rebateGroupAggregatorNdc.success.customerNdcDestinationDeleted'
				})
			)
			.with(
				NdcDestinationHistoryActions.NdcDestinationHistoryTransactionsBulkRolledBack,
				() => ({
					translationPath:
						'rebateGroupAggregatorNdc.success.ndcDestinationHistoryRolledBack'
				})
			)
			.with(NdcDestinationImportActions.ImportFileUploaded, () => {
				return {
					translationPath:
						'rebateGroupAggregatorNdc.success.ndcDestinationFileImported',
					type: NotificationType.Info
				};
			})
			.with(NdcDestinationImportActions.ImportFileRemoved, () => ({
				translationPath:
					'rebateGroupAggregatorNdc.success.ndcDestinationFileRemoved'
			}))
			.with(
				RebateAggregatorNdcRatesActions.RebateAggregatorNdcRateCreated,
				() => ({
					translationPath:
						'rebateAggregatorNdcRate.success.rebateAggregatorsNdcRateCreated'
				})
			)
			.with(
				RebateAggregatorNdcRatesActions.RebateAggregatorNdcRateUpdated,
				() => ({
					translationPath:
						'rebateAggregatorNdcRate.success.rebateAggregatorsNdcRateUpdated'
				})
			)
			.with(
				RebateAggregatorNdcRatesActions.RebateAggregatorNdcRateAdded,
				() => ({
					translationPath:
						'rebateAggregatorNdcRate.success.rebateAggregatorsNdcRateAdded'
				})
			)
			.with(
				RebateAggregatorNdcRatesActions.RebateAggregatorNdcRatesFileGenerationStarted,
				() => ({
					translationPath:
						'rebateAggregatorNdcRate.success.rebateAggregatorNdcRatesFileGenerationStarted'
				})
			)
			.with(
				RebateAggregatorNdcRatesActions.RebateAggregatorNdcRatesFileImported,
				() => ({
					translationPath:
						'rebateAggregatorNdcRate.success.rebateAggregatorsNdcRateFileImported'
				})
			)
			.with(FormularyNdcActions.FormularyNdcCreated, () => ({
				translationPath: 'formularyNdc.success.formularyNdcCreated'
			}))
			.with(FormularyNdcActions.FormularyNdcUpdated, () => ({
				translationPath: 'formularyNdc.success.formularyNdcUpdated'
			}))
			.with(
				FormularyNdcActions.FormularyNdcListFileGenerationStarted,
				() => ({
					translationPath:
						'formularyNdc.success.formularyNdcListDownloaded'
				})
			)
			.with(FormularyNdcActions.FormularyNdcListImported, () => ({
				translationPath: 'formularyNdc.success.formularyNdcListImported'
			}))
			.with(RebatesLoadActions.RebatesLoadFileAdded, () => ({
				translationPath:
					'rebatesLoad.success.loadRebatesLoadFileDetails'
			}))
			.with(
				RebatesLoadActions.RebatesLoadFileReadingStarted,
				RebatesLoadActions.ValidateRebateLoadFile,
				RebatesLoadActions.UpdatedRebatesRecordsValidationStarted,
				RebatesLoadActions.ExecuteRebateLoadConversion,
				RebatesLoadActions.ExecuteProcessRebateLoadFile,
				() => ({
					translationPath:
						'rebatesLoad.success.rebatesLoadFileValidationStarted'
				})
			)
			.with(RebatesLoadActions.AberrantQuantityRecordsResolved, () => ({
				translationPath: 'rebatesLoad.success.aberrantRecordsResolved'
			}))
			.with(RebatesLoadActions.RebateLoadCoefficientApplied, () => ({
				translationPath:
					'rebatesLoad.success.rebateLoadCoefficientApplied'
			}))
			.with(
				RebatesLoadActions.FilteredAberrantQuantityRecordsResolved,
				() => ({
					translationPath:
						'rebatesLoad.success.resolveAberrantRecords'
				})
			)
			.with(RebatesLoadActions.RebatesLoadTemplateSaved, () => ({
				translationPath: 'rebatesLoad.success.rebatesLoadTemplateSaved'
			}))
			.with(RebatesLoadActions.RebatesLoadFileDeleted, () => ({
				translationPath: 'rebatesLoad.success.fileDeleted'
			}))
			.with(RebatesLoadActions.GroupsValidatedOnMultiprocessing, () => {
				const response = data as CheckResultModel;

				if (response.result) {
					return null;
				}

				return {
					translationPath: 'rebatesLoad.warning.mappingNotEqual',
					type:            NotificationType.Warning
				};
			})
			.with(
				RebatesLoadActions.RebatesLoadFileInvalidRecordsResolvingStarted,
				() => ({
					translationPath:
						'rebatesLoad.success.recordsResolvingStarted'
				})
			)
			.with(RebatesLoadActions.InvalidRecordsBulkUpdateStarted, () => ({
				translationPath:
					'rebatesLoad.success.invalidRecordsBulkUpdateStarted'
			}))
			.with(
				RebatesLoadActions.FileDuplicatesResolved,
				RebatesLoadActions.DuplicatesResolved,
				() => ({
					translationPath: 'rebatesLoad.success.duplicateResolved'
				})
			)
			.with(
				RebatesLoadActions.AllFileDuplicatesResolved,
				RebatesLoadActions.AllDuplicatesResolved,
				() => ({
					translationPath: 'rebatesLoad.success.allDuplicatesResolved'
				})
			)
			.with(RebatesLoadActions.RebateLoadQuantitiesSetted, () => ({
				translationPath: 'rebatesLoad.success.quantitiesUpdated'
			}))
			.with(RebatesLoadActions.FilteredRecordsMarkedAsDeleted, () => ({
				translationPath: 'rebatesLoad.success.markRowsAsDeleted'
			}))
			.with(RebatesLoadActions.SubmissionPeriodChanged, () => ({
				translationPath: 'rebatesLoad.success.submissionPeriodChanged'
			}))
			.with(
				RebatesLoadActions.RebatesLoadFileInvalidRecordsByErrorCodeDeleted,
				() => ({
					translationPath:
						'rebatesLoad.success.recordsByErrorCodeDeleted'
				})
			)
			.with(
				RebatesLoadActions.RebatesInvalidRecordsFileGenerationStarted,
				() => ({
					translationPath:
						'rebatesLoad.success.rebatesInvalidRecordsFileGenerationStarted'
				})
			)
			.with(
				RebatesLoadActions.FileDuplicatesFileGenerationStarted,
				() => ({
					translationPath:
						'rebatesLoad.success.fileDuplicatesFileGenerationStarted'
				})
			)
			.with(RebatesLoadActions.DuplicatesFileGenerationStarted, () => ({
				translationPath:
					'rebatesLoad.success.duplicatesFileGenerationStarted'
			}))
			.with(RebatesLoadActions.LocationExcluded, () => ({
				translationPath: 'rebatesLoad.success.locationExcluded'
			}))
			.with(RebatesLoadActions.LocationsExcluded, () => ({
				translationPath: 'rebatesLoad.success.locationsExcluded'
			}))
			.with(RebatesLoadActions.AllLocationsExcludingStarted, () => ({
				translationPath: 'rebatesLoad.success.locationsExcludingStarted'
			}))
			.with(RebatesLoadActions.LocationsBulkOperationFinished, () => ({
				translationPath:
					'rebatesLoad.success.locationsExcludingFinished'
			}))
			.with(
				RebatesLoadActions.NotReviewedLocationsFileGenerationStarted,
				() => ({
					translationPath:
						'rebatesLoad.success.notReviewedLocationsFileGenerationStarted'
				})
			)
			.with(RebatesUpdateActions.RebatesUpdated, () => ({
				translationPath: 'rebatesUpdate.success.rebatesUpdated'
			}))
			.with(RebatesUpdateActions.RebatesBulkUpdatedStarted, () => ({
				translationPath:
					'rebatesUpdate.success.rebatesBulkUpdateStarted'
			}))
			.with(RebatesUpdateActions.RebateDeleted, () => ({
				translationPath: 'rebatesUpdate.success.rebateDeleted'
			}))
			.with(RebatesUpdateActions.RebatesBulkOperationFinished, () => ({
				translationPath: 'rebatesUpdate.success.bulkOperationFinished'
			}))
			.with(
				RebatesReSubmissionActions.RebatesReSubmissionFilesUploaded,
				() => ({
					translationPath:
						'rebatesReSubmission.success.rebatesReSubmissionFilesUploaded'
				})
			)
			.with(ReportActions.ReportGenerated, () => ({
				translationPath: 'report.success.generationStarted'
			}))
			.with(SubmissionFilesActions.SubmissionFileGenerated, () => ({
				translationPath: 'submissionFiles.success.generationStarted'
			}))
			.with(
				SubmissionFilesActions.ProductionSubmissionFileGenerated,
				() => ({
					translationPath:
						'submissionFiles.success.productionGenerationStarted'
				})
			)
			.with(
				SubmissionFilesActions.ProductionSubmissionFileSubmitted,
				() => ({
					translationPath:
						'submissionFiles.success.productionSubmissionStarted'
				})
			)
			.with(
				SubmissionFilesActions.ProductionSubmissionFileFinalized,
				() => ({
					translationPath:
						'submissionFiles.success.finalizationStarted'
				})
			)
			.with(ReportActions.ReportFileDownloaded, () => ({
				translationPath: 'report.success.downloaded'
			}))
			.with(
				NdcReplacementMappingsActions.NdcReplacementMappingCreated,
				() => ({
					translationPath:
						'ndcReplacementMappings.success.mappingCreated'
				})
			)
			.with(
				NdcReplacementMappingsActions.NdcReplacementMappingUpdated,
				() => ({
					translationPath:
						'ndcReplacementMappings.success.mappingUpdated'
				})
			)
			.with(LineOfBusinessActions.LineOfBusinessCreated, () => ({
				translationPath: 'lineOfBusiness.success.lobCreated'
			}))
			.with(LineOfBusinessActions.LineOfBusinessUpdated, () => ({
				translationPath: 'lineOfBusiness.success.lobUpdated'
			}))
			.with(LineOfBusinessActions.LineOfBusinessDeleted, () => ({
				translationPath: 'lineOfBusiness.success.lobDeleted'
			}))
			.with(
				LineOfBusinessSubtypeActions.LineOfBusinessSubtypeCreated,
				() => ({
					translationPath:
						'lineOfBusiness.subtypes.success.lobSubtypeCreated'
				})
			)
			.with(
				LineOfBusinessSubtypeActions.LineOfBusinessSubtypeUpdated,
				() => ({
					translationPath:
						'lineOfBusiness.subtypes.success.lobSubtypeUpdated'
				})
			)
			.with(
				LineOfBusinessSubtypeActions.LineOfBusinessSubtypeDeleted,
				() => ({
					translationPath:
						'lineOfBusiness.subtypes.success.lobSubtypeDeleted'
				})
			)
			.with(
				PayorsActions.CustomerPayorsReviewed,
				PayorsActions.CustomerFilteredPayorsReviewed,
				() => ({
					translationPath: 'payors.success.customerPayorsReviewed'
				})
			)
			.with(
				PayorsActions.CustomerPayorsExcluded,
				PayorsActions.CustomerFilteredPayorsExcluded,
				() => ({
					translationPath: 'payors.success.customerPayorsExcluded'
				})
			)
			.with(
				UnbreakableQuantityActions.UnbreakableQuantityCreated,
				() => ({
					translationPath:
						'unbreakableQuantity.success.unbreakableQuantityCreated'
				})
			)
			.with(
				UnbreakableQuantityActions.UnbreakableQuantityUpdated,
				() => ({
					translationPath:
						'unbreakableQuantity.success.unbreakableQuantityUpdated'
				})
			)
			.with(
				UnbreakableQuantityActions.UnbreakableQuantityDeleted,
				() => ({
					translationPath:
						'unbreakableQuantity.success.unbreakableQuantityDeleted'
				})
			)
			.with(
				RebatesLoadActions.PharmacyIdReviewBulkActionExecuted,
				() => ({
					translationPath:
						'rebatesLoad.success.pharmacyIdReviewActionExecutionStarted'
				})
			)
			.with(RebateGroupActions.RebateGroupCreated, () => ({
				translationPath: 'rebateGroup.success.rebateGroupCreated'
			}))
			.with(RebateGroupActions.RebateGroupUpdated, () => ({
				translationPath: 'rebateGroup.success.rebateGroupUpdated'
			}))
			.with(RebateGroupActions.RebateGroupDeleted, () => ({
				translationPath: 'rebateGroup.success.rebateGroupDeleted'
			}))
			.with(ExcludedPharmacyActions.ExcludedPharmacyCreated, () => ({
				translationPath:
					'excludedPharmacy.success.excludedPharmacyCreated'
			}))
			.with(ExcludedPharmacyActions.ExcludedPharmacyUpdated, () => ({
				translationPath:
					'excludedPharmacy.success.excludedPharmacyUpdated'
			}))
			.with(ExcludedPharmacyActions.ExcludedPharmacyDeleted, () => ({
				translationPath:
					'excludedPharmacy.success.excludedPharmacyDeleted'
			}))
			.with(
				InOutpatientDefinitionActions.InOutpatientDefinitionUpdated,
				InOutpatientDefinitionActions.InOutpatientDefinitionsUpdated,
				() => ({
					translationPath:
						'inOutpatientDefinition.success.inOutpatientDefinitionUpdated'
				})
			)
			.with(
				InOutpatientDefinitionActions.InOutpatientDefinitionsUpdateStarted,
				() => ({
					translationPath:
						'inOutpatientDefinition.success.inOutpatientDefinitionUpdateStarted'
				})
			)
			.with(
				InOutpatientDefinitionActions.InOutpatientDefinitionsBulkOperationFinished,
				() => ({
					translationPath:
						'inOutpatientDefinition.success.bulkOperationFinished'
				})
			)
			.with(NonPatientExclusionActions.CreateNonPatientExclusion, () => ({
				translationPath:
					'nonPatientExclusion.success.nonPatientExclusionCreated'
			}))
			.with(
				NonPatientExclusionActions.NonPatientExclusionUpdated,
				() => ({
					translationPath:
						'nonPatientExclusion.success.nonPatientExclusionUpdated'
				})
			)
			.with(
				NonPatientExclusionActions.NonPatientExclusionDeleted,
				() => ({
					translationPath:
						'nonPatientExclusion.success.nonPatientExclusionDeleted'
				})
			)
			.with(SubmissionPeriodActions.SubmissionPeriodCreated, () => ({
				translationPath:
					'submissionPeriod.success.submissionPeriodCreated'
			}))
			.with(SubmissionPeriodActions.SubmissionPeriodUpdated, () => ({
				translationPath:
					'submissionPeriod.success.submissionPeriodUpdated'
			}))
			.with(SubmissionPeriodActions.SubmissionPeriodDeleted, () => ({
				translationPath:
					'submissionPeriod.success.submissionPeriodDeleted'
			}))
			.with(
				ProcessingConfigActions.CustomerConfigCreated,
				ProcessingConfigActions.MappingConfigCreated,
				ProcessingConfigActions.ValidationConfigCreated,
				ProcessingConfigActions.DuplicateConfigCreated,
				ProcessingConfigActions.AggregationConfigCreated,
				ProcessingConfigActions.ConversionConfigCreated,
				ProcessingConfigActions.ProcessingConfigCreated,
				() => ({
					translationPath: 'processingConfig.success.configCreated'
				})
			)
			.with(
				ProcessingConfigActions.CustomerConfigUpdated,
				ProcessingConfigActions.MappingConfigUpdated,
				ProcessingConfigActions.ValidationConfigUpdated,
				ProcessingConfigActions.DuplicateConfigUpdated,
				ProcessingConfigActions.AggregationConfigUpdated,
				ProcessingConfigActions.ConversionConfigUpdated,
				ProcessingConfigActions.ProcessingConfigUpdated,
				() => ({
					translationPath: 'processingConfig.success.configUpdated'
				})
			)
			.with(
				ProcessingConfigActions.CustomerConfigDeleted,
				ProcessingConfigActions.MappingConfigDeleted,
				ProcessingConfigActions.ValidationConfigDeleted,
				ProcessingConfigActions.DuplicateConfigDeleted,
				ProcessingConfigActions.AggregationConfigDeleted,
				ProcessingConfigActions.ConversionConfigDeleted,
				ProcessingConfigActions.ProcessingConfigDeleted,
				() => ({
					translationPath: 'processingConfig.success.configDeleted'
				})
			)
			.with(SubmissionOverviewActions.SubmissionTaskUpdated, () => ({
				translationPath:
					'submissionOverview.success.submissionTaskUpdated'
			}))

			.otherwise(() => null);
	}
}
